import { useCallback, useEffect, useState } from 'react';
import { DotLoading, Empty, List, Tag } from 'antd-mobile';
import { LeftOutline, RightOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, supabase, type VehicleInspection } from '@ubus/shared';
import { useAuth } from '../hooks/useAuth';

type HistoryRow = VehicleInspection & {
  vehicles?: {
    fleet_number?: string | null;
    registration_plate?: string | null;
  } | null;
};

function vehicleLabel(row: HistoryRow): string {
  const plate = row.vehicles?.registration_plate?.trim();
  const fleet = row.vehicles?.fleet_number?.trim();
  if (plate && fleet) return `${plate} (${fleet})`;
  if (plate || fleet) return plate || fleet || '—';
  return row.work_fields?.rego?.trim() || '—';
}

function severityTagColor(v: string | null | undefined): 'success' | 'warning' | 'danger' | 'default' {
  if (v === 'blocked') return 'danger';
  if (v === 'pass_with_notes') return 'warning';
  if (v === 'pass') return 'success';
  return 'default';
}

function InspectionHistory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { driverId } = useAuth();
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!driverId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vehicle_inspections')
        .select('*, vehicles(fleet_number, registration_plate)')
        .eq('driver_id', driverId)
        .eq('inspection_type', 'pre-trip')
        .not('declaration_accepted_at', 'is', null)
        .order('inspection_date', { ascending: false })
        .order('declaration_accepted_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      setRows((data || []) as HistoryRow[]);
    } catch (err) {
      console.error('[InspectionHistory]', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="driver-detail">
      <header className="driver-detail-nav">
        <button
          type="button"
          className="driver-detail-nav-back"
          onClick={() => navigate('/profile')}
          aria-label={t('common.back')}
        >
          <LeftOutline fontSize={22} />
        </button>
        <h1 className="driver-detail-nav-title">{t('inspectionHistory.title')}</h1>
        <span className="driver-detail-nav-spacer" aria-hidden="true" />
      </header>

      <div className="driver-section" style={{ padding: '8px 0 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <DotLoading color="primary" />
          </div>
        ) : rows.length === 0 ? (
          <Empty description={t('inspectionHistory.empty')} />
        ) : (
          <List>
            {rows.map((row) => (
              <List.Item
                key={row.id}
                description={
                  <div style={{ marginTop: 4 }}>
                    <div>{vehicleLabel(row)}</div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>
                      {row.declaration_accepted_at
                        ? formatDateTime(row.declaration_accepted_at)
                        : row.inspection_date}
                    </div>
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    {row.severity_result ? (
                      <Tag color={severityTagColor(row.severity_result)} fill="outline">
                        {t(`inspectionHistory.severity.${row.severity_result}`, {
                          defaultValue: row.severity_result,
                        })}
                      </Tag>
                    ) : null}
                    <Tag
                      color={row.after_completed_at ? 'success' : 'default'}
                      fill="outline"
                    >
                      {row.after_completed_at
                        ? t('inspectionHistory.afterDoneYes')
                        : t('inspectionHistory.afterDoneNo')}
                    </Tag>
                  </div>
                }
                arrow={<RightOutline />}
                onClick={() => navigate(`/inspection-history/${row.id}`)}
              >
                {row.inspection_date}
              </List.Item>
            ))}
          </List>
        )}
      </div>
    </div>
  );
}

export default InspectionHistory;
